import { DateTime } from "luxon";
import { type SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import type { SystemAutoCompleteOption } from "../../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import type { DropdownItem } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { isNurseActiveInDepartment } from "../../../../utils/formatters";
import { CredentialStatus, type DepartmentLocation, type LocationCredentialResponse, type Nurse } from "../../../../utils/models";
import { facilityToString, getUniqueLocations } from "../../../../utils/utilFunctions";

type UseNewCredentialPopoverStateProps = {
	defaultLocationId?: string;
	defaultNurseId?: number;
	/**
	 * If we want to not show nurses that already have credentials for the selected location
	 */
	credentials?: LocationCredentialResponse[];
};

export const useNewCredentialPopoverState = ({ defaultLocationId, defaultNurseId, credentials }: UseNewCredentialPopoverStateProps) => {
	const { chosenDepartment, hospitalData } = useHospitalData();
	const [selectedLocation, setSelectedLocation] = useState<SystemAutoCompleteOption<DepartmentLocation> | null>(null);
	const [selectedNurses, setSelectedNurses] = useState<Nurse[]>([]);
	const [expiresAt, setExpiresAt] = useState<DateTime | null>(null);
	const [status, setStatus] = useState<CredentialStatus>(CredentialStatus.WaitingForHR);

	const isValid = useMemo(() => {
		return !!selectedLocation && selectedNurses.length > 0 && selectedNurses.at(0) != null;
	}, [selectedLocation, selectedNurses]);

	const resetForm = useCallback(() => {
		setSelectedLocation(null);
		setSelectedNurses([]);
		setExpiresAt(null);
		setStatus(CredentialStatus.WaitingForHR);
	}, []);

	const handleLocationChange = useCallback((_: SyntheticEvent, value: SystemAutoCompleteOption<DepartmentLocation> | null) => {
		setSelectedLocation(value);
	}, []);

	const handleNurseChangeSingle = useCallback((value: Nurse | undefined | null) => {
		if (value) {
			setSelectedNurses([value]);
		}
	}, []);
	const handleNurseChangeMultiple = useCallback((values: Nurse[]) => {
		setSelectedNurses(values);
	}, []);

	const handleExpiresAtChange = useCallback((date: DateTime | null) => {
		setExpiresAt(date);
	}, []);

	const handleStatusChange = useCallback((value: CredentialStatus | undefined | null) => {
		if (value) {
			setStatus(value);
		}
	}, []);

	const autocompleteOptions = useMemo(() => {
		return getUniqueLocations(chosenDepartment.locations)
			.filter((location) => !!location.location)
			.sort((locA, locB) => locA.location.localeCompare(locB.location))
			.map<SystemAutoCompleteOption<DepartmentLocation>>((value) => ({
				value: value,
				label: facilityToString(value),
			}));
	}, [chosenDepartment.locations]);

	const nurseItemToString = useCallback((nurse: Nurse | null, includeId = true) => {
		return nurse ? `${nurse.lastName}, ${nurse.firstName} ${includeId ? nurse.id : ""}` : "";
	}, []);

	const nurseDropdownItems = useMemo(() => {
		const credentialsNurseIdsThatAreNotExpiredForLocation =
			credentials?.filter((cred) => (cred.expires_at ? DateTime.now() < cred.expires_at : true)).map((cred) => cred.nurse_id) ?? [];
		return hospitalData.nurses
			.filter(
				(nurse) => isNurseActiveInDepartment(nurse, chosenDepartment.department_id) && !credentialsNurseIdsThatAreNotExpiredForLocation.includes(nurse.id),
			)
			.sort((a, b) => {
				const nameA = nurseItemToString(a).toLowerCase();
				const nameB = nurseItemToString(b).toLowerCase();
				return nameA.localeCompare(nameB);
			})
			.map<DropdownItem<Nurse>>((nurse) => ({
				value: nurse,
				label: nurseItemToString(nurse, false),
				id: nurse.id,
			}));
	}, [hospitalData.nurses, nurseItemToString, credentials, chosenDepartment.department_id]);

	const statusDropdownItems = useMemo<DropdownItem<CredentialStatus>[]>(() => {
		return [
			{ value: CredentialStatus.WaitingForHR, label: CredentialStatus.WaitingForHR, id: CredentialStatus.WaitingForHR },
			{
				value: CredentialStatus.WaitingForFacilityApproval,
				label: CredentialStatus.WaitingForFacilityApproval,
				id: CredentialStatus.WaitingForFacilityApproval,
			},
			{ value: CredentialStatus.Approved, label: CredentialStatus.Approved, id: CredentialStatus.Approved },
		];
	}, []);

	// Set default location if provided
	useEffect(() => {
		if (defaultLocationId && autocompleteOptions.length > 0 && !selectedLocation) {
			const defaultOption = autocompleteOptions.find((opt) => opt.value.id === defaultLocationId);
			if (defaultOption) {
				setSelectedLocation(defaultOption);
			}
		}
	}, [defaultLocationId, autocompleteOptions, selectedLocation]);

	// Set default nurse if provided
	useEffect(() => {
		if (defaultNurseId && nurseDropdownItems.length > 0 && selectedNurses.length === 0) {
			const defaultOption = nurseDropdownItems.find((opt) => opt.value.id === defaultNurseId);
			if (defaultOption) {
				setSelectedNurses([defaultOption.value]);
			}
		}
	}, [defaultNurseId, nurseDropdownItems, selectedNurses]);

	return {
		selectedLocation,
		selectedNurses,
		expiresAt,
		status,
		isValid,
		resetForm,
		handleLocationChange,
		handleNurseChangeSingle,
		handleNurseChangeMultiple,
		handleExpiresAtChange,
		handleStatusChange,
		autocompleteOptions,
		nurseDropdownItems,
		statusDropdownItems,
		nurseItemToString,
	};
};
